<template>
  <div class="creation-demand-step-salary">
    <VeeForm ref="form" @submit="save">
      <div class="creation-salary-selection">
        <section class="row q-mb-lg step">
          <div v-if="averageSalary !== '0'">
            <ExtraTipbox class="q-my-md q-ml-md">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span v-html="$t('ETAB.PAGES.DEMANDS.CREATION.@SALARY_FORM_AVERAGE_SALARY', { amount: averageSalary })" />
            </ExtraTipbox>
          </div>
          <div class="col-2 row justify-center align-middle step-checkbox">
            <IosCheckbox :model-value="isFixedAmount" @update:model-value="setFixedAmount" />
          </div>
          <div class="col">
            <h3 class="step-title">
              {{ $filters.whitespacedPunctuation($t("ETAB.PAGES.DEMANDS.CREATION.@SALARY_TITLE")) }}
            </h3>
            <AmountOptions
              :rules="{ decimal: 2, min_value: 0 }"
              :amount="salary.getAmount()"
              icon="icon-extra-euro-sign"
              :option="salary.getPeriodicity()"
              :amount-label="$t('ETAB.PAGES.DEMANDS.CREATION.@SALARY_FORM_AMOUNT_LABEL')"
              :step="0.05"
              :is-disabled="!isFixedAmount"
              :options="periodicities"
              :options-label="$t('ETAB.PAGES.DEMANDS.CREATION.@SALARY_FORM_OPTIONS_LABEL')"
              @set-amount="setAmount($event)"
              @set-option="setPeriodicity"
            />
          </div>
        </section>

        <div>
          <section class="row step">
            <div class="col-2 row justify-center align-middle step-checkbox">
              <IosCheckbox :model-value="isRangeAmount" @update:model-value="setRangeAmount" />
            </div>
            <div class="col">
              <h3 class="step-title">
                {{ $filters.whitespacedPunctuation($t("ETAB.PAGES.DEMANDS.CREATION.@SALARY_RANGE_TITLE")) }}
              </h3>
              <AmountOptions
                :rules="{ decimal: 2, min_value: 0 }"
                :min="min"
                :max="max"
                double
                :step="0.05"
                icon="icon-extra-euro-sign"
                :option="salary.getPeriodicity()"
                :amount-label="$t('ETAB.PAGES.DEMANDS.CREATION.@SALARY_FORM_AMOUNT_LABEL')"
                :is-disabled="!isRangeAmount"
                :options="periodicities"
                :options-label="$t('ETAB.PAGES.DEMANDS.CREATION.@SALARY_FORM_OPTIONS_LABEL')"
                @set-min="setMin"
                @set-max="setMax"
                @set-option="setPeriodicity"
              />
            </div>
          </section>
        </div>

        <section class="row q-mt-xl cursor-pointer" @click="setIsFluctuating(true)">
          <div class="col-2 row justify-center align-middle">
            <IosCheckbox :model-value="isFluctuating" @update:model-value="setIsFluctuating" />
          </div>
          <div class="col">
            <h3 class="text-bold">
              {{ $t("ETAB.PAGES.DEMANDS.CREATION.@SALARY_FORM_FLUCTUATING") }}
            </h3>
            <p class="text-subtitle2 q-mb-none">
              {{ $t("ETAB.PAGES.DEMANDS.CREATION.@SALARY_FORM_FLUCTUATING_WARNING") }}
            </p>
          </div>
        </section>
      </div>
      <transition appear enter-active-class="animated fadeIn slower delay-1s" leave-active-class="animated fadeOut">
        <section class="q-footer q-layout__section--marginal fixed-bottom">
          <div>
            <QBtn
              color="secondary"
              class="full-width btn-navigation"
              :label="$t('ETAB.BUTTONS.@NEXT')"
              no-caps
              type="submit"
              :loading="loading"
              :disabled="!isSalarySet"
            />
          </div>
        </section>
      </transition>
    </VeeForm>
  </div>
</template>

<script>
  import { ICreationStepSalary, CreationStepSalaryModel } from "@core/models";
  import { NumericHelpers } from "@core/helpers";
  import { Form as VeeForm } from "vee-validate";
  import AmountOptions from "@etab/components/common/AmountOptions.component.vue";

  export default {
    components: {
      AmountOptions,
      VeeForm,
    },
    props: {
      stepAssets: {
        type: Object,
        default: () => ({}),
      },
      stepData: {
        type: Object,
        default: () => ({}),
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["saveStep"],
    data() {
      return {
        salary: new CreationStepSalaryModel(),
        averageSalary: "0",
        periodicities: [],
        isFixedAmount: false,
        isRangeAmount: false,
        isFluctuating: false,
        min: null,
        max: null,
      };
    },
    computed: {
      isSalarySet() {
        return this.salary.getAmount() > 0 || this.salary.isRangeValid() || this.salary.isFluctuating();
      },
    },
    created() {
      this.salary = new CreationStepSalaryModel(this.stepData);
      this.initSalaryData();
      this.initializeData();
    },
    methods: {
      initSalaryData() {
        this.periodicities = this.stepAssets.periodicities;
        const averageSalary = this.stepAssets.average_salary;
        this.setDefaultPeriodicity();
        if (NumericHelpers.ensureNumber({ value: averageSalary })) {
          this.averageSalary = NumericHelpers.formatNumberAsCurrency({ number: averageSalary, minimumFractionDigits: 0, maximumFractionDigits: 0 });
        }
      },
      setDefaultPeriodicity() {
        if (this.salary[ICreationStepSalary.periodicity] === undefined) {
          this.salary[ICreationStepSalary.periodicity] = this.periodicities[0].id;
        }
      },
      setAmount(value) {
        this.salary[ICreationStepSalary.amount] = value;
      },
      setSalaryRange({ min, max } = {}) {
        if (min !== undefined && max !== undefined) {
          this.salary[ICreationStepSalary.range] = { min, max };
        } else {
          this.salary[ICreationStepSalary.range] = undefined;
        }
      },
      setPeriodicity(value) {
        this.salary[ICreationStepSalary.periodicity] = value;
      },
      setFluctuating(value) {
        this.salary[ICreationStepSalary.fluctuating] = value;
      },
      initializeData() {
        if (this.salary.getAmount() >= 0) {
          this.isFixedAmount = true;
        }
        if (this.salary.getRange()) {
          this.isRangeAmount = true;
          const { min, max } = this.salary.getRange() || {};
          this.min = min;
          this.max = max;
        }
        if (this.salary.isFluctuating()) {
          this.isFluctuating = true;
        }
        if (!this.salary.isFluctuating() && !this.salary.getRange() && !this.salary.getAmount()) {
          // RG: we select fixed salary by default
          this.isFixedAmount = true;
        }
      },
      resetAmount() {
        this.isFixedAmount = false;
        this.setAmount(undefined);
      },
      resetRange() {
        this.min = null;
        this.max = null;
        this.isRangeAmount = false;
        this.setSalaryRange(undefined);
      },
      resetFluctuating() {
        this.isFluctuating = false;
        this.setFluctuating(undefined);
      },
      setFixedAmount(value) {
        this.isFixedAmount = value;
        if (!value) {
          this.resetAmount();
        }
        // reset other values
        this.resetRange();
        this.resetFluctuating();
      },
      setRangeAmount(value) {
        this.isRangeAmount = value;
        if (!value) {
          this.resetRange();
        }
        // reset other values
        this.resetAmount();
        this.resetFluctuating();
      },
      setIsFluctuating(value) {
        this.isFluctuating = value;
        this.setFluctuating(true);
        if (!value) {
          this.resetFluctuating();
        }
        // reset other values
        this.resetAmount();
        this.resetRange();
      },
      setMin(value) {
        const isValidNumber = !Number.isNaN(value);
        // we dont want to emit a NaN value
        this.min = isValidNumber ? value : null;
        this.setSalaryRange({ min: this.min, max: this.max });
      },
      setMax(value) {
        const isValidNumber = !Number.isNaN(value);
        // we dont want to emit a NaN value
        this.max = isValidNumber ? value : null;
        this.setSalaryRange({ min: this.min, max: this.max });
      },
      save() {
        if (this.isSalarySet) {
          this.$emit("saveStep", {
            [ICreationStepSalary.fluctuating]: this.salary.isFluctuating(),
            [ICreationStepSalary.range]: !this.salary.isFluctuating() && !this.salary.getAmount() ? this.salary.getRange() : undefined,
            [ICreationStepSalary.amount]: !this.salary.isFluctuating() ? this.salary.getAmount() : undefined,
            [ICreationStepSalary.periodicity]: !this.salary.isFluctuating() ? this.salary.getPeriodicity() : undefined,
          });
        }
      },
    },
  };
</script>

<style lang="scss">
  .creation-salary-selection {
    .step {
      align-items: center;
      .step-checkbox {
        height: 75px;
        margin-top: 50px;
      }
      .step-title {
        text-align: left;
        justify-content: left !important; // overwrite default step style
      }
    }
  }
</style>
